import type { FetchCoursesParams as CoreFetchCoursesParams } from '@e-learning/core';
import type {
  CourseListItem,
  DynamicModalFieldConfig,
  DynamicModalFormValues,
  FetchCoursesParams,
} from '@e-learning/shared';
import {
  Courses,
  DynamicModal,
  Toast,
  iconCheckWhiteModalCourses,
} from '@e-learning/shared';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useCourses } from './hooks';

/** Formato retornado por fetchCourseCategories (v1/CourseCategory) */
interface CourseCategoryItem {
  id: string;
  translations: { languageCode: string; name: string }[];
}

/** Formato retornado por fetchCourseStatus (v1/Course/status) */
interface CourseStatusItem {
  id: number;
  name: string;
}

function getCourseStatusId(course: CourseListItem): string {
  const raw = course.status;
  if (typeof raw === 'number' && !Number.isNaN(raw)) return String(raw);
  if (typeof raw === 'string' && raw.trim() !== '') {
    const n = Number(raw);
    if (!Number.isNaN(n)) return String(n);
  }
  if (course.statusId != null) return String(course.statusId);
  return '';
}

function getCourseRowTitle(course: CourseListItem, lang: string): string {
  if (typeof course.name === 'string' && course.name.trim()) {
    return course.name.trim();
  }
  if (typeof course.title === 'string' && course.title.trim()) {
    return course.title.trim();
  }
  const preferred = lang.toLowerCase().startsWith('es') ? 'es-ES' : 'en-US';
  const fallback = preferred === 'en-US' ? 'es-ES' : 'en-US';
  const items = course.translations;
  if (Array.isArray(items)) {
    const list = items as { languageCode?: string; title?: string }[];
    const t1 = list.find(i => i.languageCode === preferred)?.title;
    if (t1?.trim()) return t1.trim();
    const t2 = list.find(i => i.languageCode === fallback)?.title;
    if (t2?.trim()) return t2.trim();
    const first = list.map(i => i.title).find(s => typeof s === 'string' && s.trim());
    if (first?.trim()) return first.trim();
  }
  return course.id;
}

export default function CoursesPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const {
    fetchCourses: fetchCoursesApi,
    fetchCourseCategories,
    fetchCourseStatus,
    updateCourseStatus,
    archiveCourse,
  } = useCourses();

  const [categoryList, setCategoryList] = useState<CourseCategoryItem[]>([]);
  const [statusList, setStatusList] = useState<CourseStatusItem[]>([]);
  const [courseToDelete, setCourseToDelete] = useState<CourseListItem | null>(
    null
  );
  const [courseForStatusEdit, setCourseForStatusEdit] =
    useState<CourseListItem | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isStatusUpdating, setIsStatusUpdating] = useState(false);
  const [listKey, setListKey] = useState(0);
  const [toastMessage, setToastMessage] = useState<{
    text: string;
    variant: 'success' | 'error';
  } | null>(null);

  /** Adapta parâmetros do shared para a API do core (filtros: CourseTitle, CourseCategoryId, Status, DateCreated). */
  const fetchCourses = useCallback(
    async (params: FetchCoursesParams) => {
      const statusName = params.statusId
        ? statusList.find(s => String(s.id) === params.statusId)?.name
        : undefined;
      const coreParams: CoreFetchCoursesParams = {
        page: params.page,
        limit: params.limit,
        search: params.search,
        courseCategoryId: params.categoryId,
        status: statusName,
        dateCreated: params.dateCreated,
        orderByCreatedAtDesc: true,
      };
      const res = await fetchCoursesApi(coreParams);
      const createdMs = (c: CourseListItem) =>
        new Date(c.createdAt ?? c.created ?? 0).getTime();
      const data = [...(res.data ?? [])].sort(
        (a, b) => createdMs(b) - createdMs(a)
      );
      return { ...res, data };
    },
    [fetchCoursesApi, statusList]
  );

  const desiredLanguageCode = i18n.language === 'es' ? 'es-ES' : 'en-US';

  const categoryOptions = useMemo(
    () =>
      categoryList.map(c => ({
        value: c.id,
        label:
          c.translations?.find(tr => tr.languageCode === desiredLanguageCode)
            ?.name ??
          c.translations?.[0]?.name ??
          c.id,
      })),
    [categoryList, desiredLanguageCode]
  );

  const statusOptions = useMemo(
    () => statusList.map(s => ({ value: String(s.id), label: s.name })),
    [statusList]
  );

  const statusModalOptions = useMemo(
    () =>
      statusList.map(s => ({
        value: String(s.id),
        label: s.name,
      })),
    [statusList]
  );

  const statusModalFields: DynamicModalFieldConfig[] = useMemo(
    () => [
      {
        name: 'courseStatus',
        label: t('courses.status_modal_field_label'),
        type: 'select' as DynamicModalFieldConfig['type'],
        placeholder: t('courses.status_modal_field_placeholder'),
        optional: false,
        options: statusModalOptions,
      },
    ],
    [t, statusModalOptions]
  );

  const statusModalInitialValues: Partial<DynamicModalFormValues> = useMemo(
    () =>
      courseForStatusEdit
        ? { courseStatus: getCourseStatusId(courseForStatusEdit) }
        : {},
    [courseForStatusEdit]
  );

  useEffect(() => {
    let cancelled = false;
    fetchCourseCategories({ includeInactive: false })
      .then((list: CourseCategoryItem[]) => {
        if (!cancelled) setCategoryList(list);
      })
      .catch(() => {
        if (!cancelled) setCategoryList([]);
      });
    return () => {
      cancelled = true;
    };
  }, [fetchCourseCategories, i18n.language]);

  useEffect(() => {
    let cancelled = false;
    fetchCourseStatus()
      .then((list: CourseStatusItem[]) => {
        if (!cancelled) setStatusList(list);
      })
      .catch(() => {
        if (!cancelled) setStatusList([]);
      });
    return () => {
      cancelled = true;
    };
  }, [fetchCourseStatus, i18n.language]);

  const handleCreateCourse = useCallback(() => {
    navigate('/admin/courses/new');
  }, [navigate]);

  const handleEdit = useCallback(
    (course: CourseListItem) => {
      navigate(`/admin/courses/${course.id}/edit`);
    },
    [navigate]
  );

  const handleEditStatus = useCallback((course: CourseListItem) => {
    setCourseForStatusEdit(course);
  }, []);

  const handleSaveStatus = useCallback(
    async (values: DynamicModalFormValues) => {
      if (!courseForStatusEdit || isStatusUpdating) return;
      const nextId = Number(values.courseStatus);
      if (!Number.isFinite(nextId)) {
        setToastMessage({
          text: t('courses.status_update_error'),
          variant: 'error',
        });
        return;
      }
      const currentId = getCourseStatusId(courseForStatusEdit);
      if (String(nextId) === currentId) {
        setCourseForStatusEdit(null);
        return;
      }
      setIsStatusUpdating(true);
      try {
        await updateCourseStatus(courseForStatusEdit.id, { status: nextId });
        setCourseForStatusEdit(null);
        setListKey(k => k + 1);
        setToastMessage({
          text: t('courses.status_update_success'),
          variant: 'success',
        });
      } catch {
        setToastMessage({
          text: t('courses.status_update_error'),
          variant: 'error',
        });
      } finally {
        setIsStatusUpdating(false);
      }
    },
    [courseForStatusEdit, isStatusUpdating, updateCourseStatus, t]
  );

  const handleDelete = useCallback((course: CourseListItem) => {
    setCourseToDelete(course);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!courseToDelete || isDeleting) return;
    setIsDeleting(true);
    try {
      await archiveCourse(courseToDelete.id);
      setCourseToDelete(null);
      setListKey(k => k + 1);
      setToastMessage({
        text: t('courses.delete_success'),
        variant: 'success',
      });
    } catch {
      setToastMessage({
        text: t('courses.delete_error'),
        variant: 'error',
      });
    } finally {
      setIsDeleting(false);
    }
  }, [archiveCourse, courseToDelete, isDeleting, t]);

  useEffect(() => {
    if (!toastMessage) return;
    const id = setTimeout(() => setToastMessage(null), 4000);
    return () => clearTimeout(id);
  }, [toastMessage]);

  return (
    <div data-e2e="page-admin-courses">
      {toastMessage && (
        <Toast
          message={toastMessage.text}
          variant={toastMessage.variant}
          role="alert"
        />
      )}
      <Courses
        key={listKey}
        fetchCourses={fetchCourses}
        categoryOptions={categoryOptions}
        statusOptions={statusOptions}
        onCreateCourse={handleCreateCourse}
        onEdit={handleEdit}
        onEditStatus={handleEditStatus}
        onDelete={handleDelete}
      />
      <DynamicModal
        title={
          courseForStatusEdit
            ? t('courses.status_modal_title', {
                name: getCourseRowTitle(courseForStatusEdit, i18n.language),
              })
            : ''
        }
        description={t('courses.status_modal_description')}
        open={!!courseForStatusEdit}
        onClose={() => !isStatusUpdating && setCourseForStatusEdit(null)}
        closeOnOverlayClick={false}
        initialValues={statusModalInitialValues}
        fields={statusModalFields}
        dataE2ePrefix="courses-status-edit"
        actions={[
          {
            label: t('courses.modal_action_cancel'),
            onClick: () => {
              if (!isStatusUpdating) setCourseForStatusEdit(null);
            },
            variant: 'secondary',
            outlined: true,
          },
          {
            label: isStatusUpdating
              ? t('courses.status_modal_saving')
              : t('courses.status_modal_save'),
            onClick: handleSaveStatus,
            variant: 'primary',
            iconSrc: iconCheckWhiteModalCourses,
            iconSize: 18,
            iconAlt: t('courses.status_modal_save'),
          },
        ]}
      />
      <DynamicModal
        title={
          courseToDelete
            ? t('courses.delete_confirm_title', {
                name:
                  courseToDelete.name ??
                  courseToDelete.title ??
                  courseToDelete.id,
              })
            : ''
        }
        description={t('courses.delete_confirm_message')}
        compact
        open={!!courseToDelete}
        onClose={() => !isDeleting && setCourseToDelete(null)}
        closeOnOverlayClick={false}
        fields={[]}
        actions={[
          {
            label: t('courses.modal_action_cancel'),
            onClick: () => setCourseToDelete(null),
            variant: 'secondary',
          },
          {
            label: isDeleting
              ? t('courses.modal_action_deleting')
              : t('courses.modal_action_delete'),
            onClick: handleConfirmDelete,
            variant: 'primary',
          },
        ]}
        dataE2ePrefix="courses-delete-confirm"
      />
    </div>
  );
}
